import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import { StoreContextService } from '../services/store-context.service';
import { AuthService } from '../services/auth.service';
import { TenantService } from '../services/tenant.service';

/**
 * Screens that exist only for catering and split-delivery stores (Store
 * Calendar and its month view). Opening one by URL on an instant or
 * subscription store lands here instead: the calendar lives under Products,
 * so the product list is the nearest page that applies to every store.
 */
export const CATERING_OR_SPLIT_FALLBACK = '/products';

/**
 * How long a direct link waits for the store list before giving up. The
 * store type comes from that list; if it never answers, the page stays
 * hidden (fail closed) rather than holding the navigation forever.
 */
export const STORE_TYPE_WAIT_MS = 15000;

/**
 * Route guard for catering / split-delivery only pages.
 *
 * Same source of truth as every other gate: `StoreContextService.isCateringOrSplit`
 * (see the products-list bulk-days gate). It is false while the type is
 * loading or unknown, so this guard never lets the page through on a guess.
 *
 * WHY IT WAITS INSTEAD OF REDIRECTING STRAIGHT AWAY
 *
 * On a hard refresh of /products/store-calendar/schedule the store list has
 * not been requested yet — AppComponent calls `storeCtx.load()` on the first
 * NavigationEnd, which cannot happen while this guard is holding the
 * navigation. Redirecting on "not loaded" would bounce every catering
 * operator who refreshes the page. So the guard starts the load itself
 * (`load()` is idempotent) and decides once the list is in.
 *
 * It only starts that load for a signed-in user with a tenant. AuthGuard runs
 * alongside this one and does the redirect to login; firing GET /stores
 * without credentials would only add a failing request.
 */
export const cateringOrSplitGuard: CanActivateFn = (): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const storeCtx = inject(StoreContextService);
  const router = inject(Router);
  const fallback = () => router.parseUrl(CATERING_OR_SPLIT_FALLBACK);

  if (storeCtx.isCateringOrSplit) return true;
  // The list is in and the answer is "not catering / split" or "cannot tell".
  if (storeCtx.loadComplete) return fallback();

  const tenant = inject(TenantService);
  const auth = inject(AuthService);
  if (!tenant.isSet || !auth.isAuthenticated()) return false;

  storeCtx.load();
  return storeCtx.loadComplete$.pipe(
    filter(done => done === true),
    take(1),
    timeout(STORE_TYPE_WAIT_MS),
    map(() => (storeCtx.isCateringOrSplit ? true : fallback())),
    catchError(() => of(fallback())),
  );
};
